<template>
	<div :key="key" class="d-flex flex-row justify-center align-center grey lighten-3 rounded px-1">

		<AsiBtn v-if="!disabled" :icon="icons.remove" :disabled="disabled" :small="small" :x-small="xSmall"
		        @click.stop="decrease"/>

		<div class="pa-1" style="max-width: 5rem">
			<AsiTextFieldSimple v-if="!small && !xSmall" :disabled="disabled" class="text-right"
			                    :value="displayValue" @change="displayValue = $event"
			                    @focus="$event.target.select()" @keydown="handleKeyDown"/>

			<v-menu v-else v-model="menuOpen" :disabled="disabled" :close-on-content-click="false"
			        max-width="10rem" bottom offset-y left transition="false" >
				<template v-slot:activator="{on, attrs}">
					<div v-on="on" v-bind="attrs" class="d-flex flex-row align-center mx-1">
						<span class="black--text font-weight-medium">{{ valueInternal }}</span>
						<small v-if="unit !== null" class="ml-1">{{ unitName }}</small>
					</div>
				</template>
				<div class="grey lighten-3 pa-2 d-flex flex-row align-center">
					<AsiTextFieldSimple :disabled="disabled" class="text-right"
					                    :value="displayValue" @change="displayValue = $event"
					                    @focus="$event.target.select()" @keydown="handleKeyDown" ref="menuInput"/>
					<small v-if="unit !== null" class="ml-1 grey--text">{{ unitName }}</small>
				</div>
			</v-menu>
		</div>
		<small v-if="!small && !xSmall && unit !== null" class="mx-1">{{ unitName }}</small>

		<AsiBtn v-if="!disabled" :icon="icons.add" :disabled="disabled" :small="small" :x-small="xSmall"
		        @click.stop="increase"/>
	</div>
</template>

<script lang="ts">
	import Vue from 'vue';
	import {Component, Emit, Prop, Watch} from "vue-property-decorator";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import Icon from "@/plugins/icons";
	import AsiTextField from "@/components/common/AsiTextField";
	import AsiTextFieldSimple from "@/components/common/AsiTextFieldSimple";
	import EnumHelper from "@/helpers/EnumHelper";
	import {UnitOfMeasure} from "@/helpers/constants";
	import StringHelper from "@/helpers/StringHelper";

	@Component({
		components: {AsiTextFieldSimple, AsiTextField, AsiBtn}
	})
	export default class ItemQuantitySelection extends Vue {

		@Prop({type: Number, required: true})
		public value!: number;

		@Prop({type: Number, required: true})
		public defaultValue!: number;

		@Prop({type: Number, default: 1})
		public step!: number;

		@Prop({type: Number, default: null})
		public unit!: number | null;

		@Prop({type: Boolean, default: false})
		public small!: boolean;

		@Prop({type: Boolean, default: false})
		public xSmall!: boolean;

		@Prop({type: Boolean, default: false})
		public debounce!: boolean;

		@Prop({type: Boolean, default: false})
		public disabled!: boolean;

		@Prop({type: Boolean, default: false})
		public isCartSelector!: boolean;

		private icons = Icon;
		private valueInternal: number = 1;
		private menuOpen: boolean = false;
		private debounceTimeout: any = null;
		private key: number = 1;

		public mounted(): void {
			if (this.isCartSelector) {
				this.valueInternal = this.value;
			} else {
				this.valueInternal = this.defaultValue;
			}
		}

		public forceUpdate() {
			this.key++;
		}

		private get displayValue(): number {
			return Math.max(this.valueInternal, this.defaultValue);
		}

		private set displayValue(value: string | number) {
			const oldValue = this.valueInternal;
			this.handleInput(value);
			this.forceUpdate();

			if (this.valueInternal !== oldValue) {
				this.input();
			}
		}

		private get unitName(): string | null {
			return EnumHelper.textFromValue(UnitOfMeasure, this.unit, true, this.valueInternal ?? 1);
		}

		private handleInput(value: number | string): void {
			let cleanedValue: string;
			let numericValue: number;

			if (typeof value === 'string') {
				cleanedValue = value.replace(/[^\d.]/g, '').trim();
				if (StringHelper.isEmpty(cleanedValue)) {
					this.valueInternal = this.defaultValue;
					return;
				}
				numericValue = Number.parseFloat(cleanedValue);
				if (Number.isNaN(numericValue)) {
					this.valueInternal = this.defaultValue;
					return;
				}
			} else {
				numericValue = parseFloat(value.toFixed(2));
			}
			if (numericValue < this.defaultValue) {
				this.valueInternal = this.defaultValue;
			} else {
				const alignedValue = numericValue % this.step === 0
					? numericValue
					: Math.floor(numericValue / this.step) * this.step;
				this.valueInternal = parseFloat(alignedValue.toFixed(2));
			}
		}

		@Watch('value', {immediate: true})
		private onValueChanged(value: number): void {
			this.handleInput(value);
			this.$emit('input', this.valueInternal);
		}

		@Watch('defaultValue', {immediate: true})
		private onDefaultValueChanged(value: number): void {
			this.valueInternal = value;
		}

		@Watch('menuOpen')
		private onMenuOpenChanged(value: boolean): void {
			console.log('MENU OPEN');
			if (value) {
				setTimeout(() => {
					const input = this.$refs.menuInput as any;
					if (input === undefined || input.$refs?.input === undefined) return;
					input.$refs.input.select();
				}, 50);
			}
		}

		@Emit('input')
		public input(): number {
			return this.valueInternal;
		}

		private handleKeyDown(event: KeyboardEvent): void {
			switch (event.key) {
				case 'ArrowUp':
				case 'ArrowRight':
				case '+':
					event.preventDefault();
					this.displayValue = (this.valueInternal + this.step);
					break;
				case 'ArrowDown':
				case 'ArrowLeft':
				case '-':
					event.preventDefault();
					this.displayValue = Math.max(this.valueInternal - this.step, this.defaultValue);
					break;
			}
		}

		private increase(): void {
			this.displayValue = (this.valueInternal + this.step);
		}

		private decrease(): void {
			this.displayValue = Math.max(this.valueInternal - this.step, this.defaultValue);
		}
	}
</script>

<style lang="scss" scoped>

</style>
